import * as Sentry from '@sentry/react'
import { useFormik } from 'formik'
import React, { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { maxSizeError, requiredError } from 'Components/Forms/FormErrors'
import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { useEdition } from 'hooks/useEdition'
import { AFIRMATIVE_VALUE } from 'utils/constants'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'
import { get } from 'utils/objectUtils'
import { cleanObjectIds, getCurrentJWToken, getEmailFromToken } from 'utils/utils'
import EditComponent from '../Fields/EditComponent'
import { InputAcceptRegulation } from '../Fields/InputAcceptRegulation'

import { CloseOutlined } from '@mui/icons-material'
import { Box } from '@mui/system'
import { AlertComponent } from 'Components/HelperComponents'
import { ModalInfo } from 'NewVersion/components/UI/Modals/Modal'
import { AddProfessionalsIconOutline } from 'NewVersion/icons/AddProfessionalsIconOutline'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import 'NewVersion/styles/Form.scss'
import { useGuillotina } from 'hooks/useGuillotina'
import { useScrollToFirstError } from 'hooks/useScrollToFirstError'
import { ACCREDITATION_TYPES_OBJ } from 'utils/accreditationUtils'
import { ACCREDITATIONS_SIZES_IMAGE } from 'utils/constants'
import Button from '../UI/Buttons/Button'

export const AddAccreditationsForm = ({
  ensType,
  submitAction,
  entity,
  loadingEntity,
  showPaidAccreditationsWarning = false,
  hasAcceptedPaidAccreditations = false,
  onPaidAccreditationsAccepted,
}) => {
  const ensTypeObj = ENS_TYPES_OBJ[ensType]
  const { edition, loading, currentEdition } = useEdition()

  const accreditationTypeObj = ACCREDITATION_TYPES_OBJ[ensType]
    ? ACCREDITATION_TYPES_OBJ[ensType]
    : ACCREDITATION_TYPES_OBJ['professional']

  const {
    data: accreditationSchemaData = {},
    isLoading: accreditationSchemaLoading,
    error: accreditationSchemaError,
  } = useGuillotina({
    path: `@types/${accreditationTypeObj?.interface}`,
  })

  if (loading || accreditationSchemaLoading || loadingEntity) {
    return <LoadingComponent />
  }

  return (
    <AddAccreditationsFormContent
      currentEdition={currentEdition}
      ensTypeObj={ensTypeObj}
      edition={edition}
      accreditationSchema={{
        data: accreditationSchemaData,
        error: accreditationSchemaError,
        loading: accreditationSchemaLoading,
      }}
      submitAction={submitAction}
      accreditationTypeObj={accreditationTypeObj}
      dataEntity={entity}
      showPaidAccreditationsWarning={showPaidAccreditationsWarning}
      hasAcceptedPaidAccreditations={hasAcceptedPaidAccreditations}
      onPaidAccreditationsAccepted={onPaidAccreditationsAccepted}
    />
  )
}

const AddAccreditationsFormContent = ({
  edition,
  accreditationSchema,
  submitAction,
  ensTypeObj,
  currentEdition,
  accreditationTypeObj,
  readOnlyForm = false,
  dataEntity,
  showPaidAccreditationsWarning = false,
  hasAcceptedPaidAccreditations = false,
  onPaidAccreditationsAccepted,
}) => {
  const [totalAccreditationsToAdd, setTotalAccreditationsToAdd] = useState(1)
  const [showPaidAccreditationsModal, setShowPaidAccreditationsModal] = useState(false)
  const { t } = useTranslation()
  const [submitError, setSubmitError] = React.useState({
    hasError: false,
    message: '',
  })

  const handleAccreditationError = (error) => {
    const err = new Error(
      `Add Accreditation form error: ${JSON.stringify(get(error, 'info', error))}`
    )
    err.name = 'Add Accreditation form error'
    Sentry.captureException(err)
    if (error && get(error, 'info', null)) {
      if (get(error, 'info.code') === 'invalid-email') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('invalid_email')}`,
        })
      } else {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('error_create_or_update_ens')}`,
        })
      }
    } else {
      setSubmitError({
        hasError: true,
        message: `Error: ${t('error_generic')}`,
      })
    }
    window.scrollTo(0, 0)
  }

  const handleAccreditationImageError = (error) => {
    const err = new Error(
      `Add Accreditation image form error: ${JSON.stringify(get(error, 'info', error))}`
    )
    err.name = 'Add Accreditation image form error'
    Sentry.captureException(err)
    setSubmitError({
      hasError: true,
      message: `Error: ${t('error_uploading_image')}`,
    })
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let finishSuccessfully = true
      setSubmitting(true)
      const valuesEntity = {}
      const token = getCurrentJWToken()

      Object.keys(values).forEach((valueKey) => {
        if (valueKey.indexOf('entity_') === 0) {
          valuesEntity[valueKey.replace('entity_', '')] = values[valueKey]
        }
      })

      const dataAccreditations = []
      const keysAccreditationForm = [...Array(totalAccreditationsToAdd).keys()]
      keysAccreditationForm.forEach((index) => {
        const valuesAccreditation = {}
        Object.keys(values).forEach((valueKey) => {
          if (valueKey.indexOf(`accreditation_${index}_`) === 0) {
            valuesAccreditation[valueKey.replace(`accreditation_${index}_`, '')] = values[valueKey]
          }
        })

        const dataAccreditation = {}
        dataAccreditation['values'] = Object.assign({}, valuesAccreditation, {
          '@type': accreditationTypeObj.interface,
          tarifa: 'estandard',
          idioma: dataEntity.idioma,
        })

        if (dataAccreditation['values']['primer_cop_fira'] === AFIRMATIVE_VALUE) {
          dataAccreditation['values']['primer_cop_fira'] = true
        } else {
          dataAccreditation['values']['primer_cop_fira'] = false
        }

        if (dataAccreditation['values']['imatge'] !== null) {
          dataAccreditation['imatge'] = dataAccreditation['values']['imatge']
          delete dataAccreditation['values']['imatge']
        }

        dataAccreditations.push(dataAccreditation)
      })

      for (let i = 0; i < dataAccreditations.length; i++) {
        const dataToSend = dataAccreditations[i]
        let path = `${edition}/${dataEntity['@name']}`
        try {
          const response = await fetchGuillotina({
            path: path,
            method: 'POST',
            data: dataToSend['values'],
            token: token,
          })
          path = `${path}/${response['@name']}`

          if (dataToSend['imatge'] && dataToSend['imatge'] instanceof File) {
            let filename = undefined
            if (dataToSend['imatge'].filename) {
              filename = dataToSend['imatge'].filename
            } else if (dataToSend['imatge'].name) {
              filename = dataToSend['imatge'].name
            }
            const fileHeaders = {
              'X-UPLOAD-EXTENSION': dataToSend['imatge'].type.split('/')[1],
              // 'X-UPLOAD-SIZE': dataToSend['imatge'].size,
              'X-UPLOAD-FILENAME-B64': btoa(unescape(encodeURIComponent(filename))),
              'Content-Type': dataToSend['imatge'].type,
            }
            await fetchGuillotina({
              path: `${path}/@upload/imatge`,
              method: 'PATCH',
              token: token,
              headers: {
                ...fileHeaders,
              },
              data: dataToSend['imatge'],
              notStringify: true,
            })
            for (let i = 0; i < ACCREDITATIONS_SIZES_IMAGE.length; i++) {
              try {
                await fetchGuillotina({
                  path: `${path}/@images/imatge/${ACCREDITATIONS_SIZES_IMAGE[i]}`,
                  method: 'PATCH',
                  token: token,
                  headers: {
                    ...fileHeaders,
                  },
                  data: dataToSend['imatge'],
                  notStringify: true,
                })
              } catch (error) {
                if (error) handleAccreditationImageError(error)
              }
            }
          }
        } catch (error) {
          finishSuccessfully = false
          handleAccreditationError(error)
        }
      }

      if (submitAction && finishSuccessfully) {
        let paymentUuid = null
        const totalIncludedAccreditations = parseInt(
          currentEdition.getConfigData()?.[ensTypeObj?.totalAccreditationAttribute],
          10
        )
        const totalAccreditationsAfterSubmit =
          (dataEntity?.total_acreditats || 0) + totalAccreditationsToAdd
        const mustShowPaymentSummary =
          Number.isFinite(totalIncludedAccreditations) &&
          totalAccreditationsAfterSubmit > totalIncludedAccreditations

        if (mustShowPaymentSummary) {
          try {
            const paymentResponse = await fetchGuillotina({
              path: `${cleanObjectIds(
                dataEntity['@id']
              )}/@search?type_name=Pagament&_sort_des=creation_date`,
              token,
            })
            paymentUuid = paymentResponse?.items?.[0]?.uuid || null
          } catch (error) {
            const err = new Error(
              `Add Accreditation payment fetch error: ${JSON.stringify(get(error, 'info', error))}`
            )
            err.name = 'Add Accreditation payment fetch error'
            Sentry.captureException(err)
          }
        }

        submitAction(paymentUuid)
      }
      setSubmitting(false)
    } catch (error) {
      const err = new Error(`Add Accreditation form error: ${error}`)
      err.name = 'Add Accreditation form error'
      Sentry.captureException(err)
    }
  }

  const totalIncludedAccreditations = useMemo(() => {
    const total = parseInt(
      currentEdition.getConfigData()?.[ensTypeObj?.totalAccreditationAttribute],
      10
    )

    return Number.isFinite(total) ? total : null
  }, [currentEdition, ensTypeObj])

  const mustWarnBeforeAddingNextAccreditation = useMemo(() => {
    if (
      !showPaidAccreditationsWarning ||
      hasAcceptedPaidAccreditations ||
      !Number.isFinite(totalIncludedAccreditations)
    ) {
      return false
    }

    const totalAccreditationsAfterAddingNext =
      Number(dataEntity?.total_acreditats || 0) + totalAccreditationsToAdd + 1

    return totalAccreditationsAfterAddingNext > totalIncludedAccreditations
  }, [
    dataEntity,
    hasAcceptedPaidAccreditations,
    showPaidAccreditationsWarning,
    totalAccreditationsToAdd,
    totalIncludedAccreditations,
  ])

  const getInitialValues = React.useMemo(() => {
    const result = {}

    const keysAccreditationForm = [...Array(totalAccreditationsToAdd).keys()]
    keysAccreditationForm.forEach((index) => {
      accreditationTypeObj?.['requiredFields'].forEach((item) => {
        result[`accreditation_${index}_${item}`] = undefined
      })
    })

    result['declaracio_acceptar'] = false
    return result
  }, [ensTypeObj])

  const formik = useFormik({
    initialValues: getInitialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validate: (values) => {
      const errors = {}

      const keysAccreditationForm = [...Array(totalAccreditationsToAdd).keys()]
      keysAccreditationForm.forEach((index) => {
        accreditationTypeObj['requiredFields'].forEach((key) => {
          if (key !== 'declaracio_acceptar') {
            const keyForm = `accreditation_${index}_${key}`
            let value = get(values, keyForm, null)
            if (typeof value === 'string') {
              value = value.trim()
            }
            if (!(keyForm in values) || !value) {
              errors[keyForm] = requiredError()
            }
          }
        })
        const keyForm = `accreditation_${index}_correu`
        let value = get(values, keyForm, null)
        if (typeof value === 'string') {
          value = value.trim()
        }
        if (value && !value.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)) {
          errors[keyForm] = t('invalid_email')
        }
        if (
          values[`accreditation_${index}_imatge`] &&
          values[`accreditation_${index}_imatge`] instanceof File &&
          values[`accreditation_${index}_imatge`].size > 4194304
        ) {
          errors[`accreditation_${index}_imatge`] = maxSizeError()
        }
      })
      if (!('declaracio_acceptar' in values) || !values['declaracio_acceptar']) {
        errors['declaracio_acceptar'] = requiredError()
      }
      return errors
    },
  })
  useScrollToFirstError(formik.errors, formik.submitCount)

  const renderInputAcceptRegulation = (key) => {
    return (
      <InputAcceptRegulation
        onChange={(e) => {
          formik.setFieldValue(key, e.target.checked)
          formik.validateForm(Object.assign({}, formik.values, { key: e.target.checked }))
        }}
        onBlur={formik.onBlur}
        checked={!!formik.values[key]}
        error={formik.touched[key] && formik.errors[key]}
        readOnly={readOnlyForm}
        name={key}
      />
    )
  }

  const resetForm = () => {
    const result = formik.values
    accreditationTypeObj['requiredFields'].forEach((item) => {
      result[`accreditation_${totalAccreditationsToAdd}_${item}`] = undefined
    })
    formik.resetForm({
      values: result,
    })
    setTotalAccreditationsToAdd(totalAccreditationsToAdd + 1)
  }

  return (
    <>
      {submitError.hasError && submitError.message && (
        <AlertComponent type="danger"> {submitError.message}</AlertComponent>
      )}
      <form
        className="Form"
        onSubmit={(values) => {
          formik.handleSubmit(values)
        }}
        data-test="formTest"
      >
        {ensTypeObj && (
          <h4 className="header-add-accreditations">
            {ensTypeObj?.['interface'] === ENS_TYPES_OBJ.premsa.ens_type_name
              ? t('press_registrations')
              : t('professional_registrations')}
          </h4>
        )}
        <p>
          {ensTypeObj?.['interface'] === ENS_TYPES_OBJ.premsa.ens_type_name
            ? t('press_registrations_subtitle')
            : t('professional_registrations_subtitle')}
        </p>
        {[...Array(totalAccreditationsToAdd).keys()].map((index) => {
          return (
            <div key={`accreditationForm_${index}`} className="acreditacio">
              <div>
                <h5>
                  {t('acreditacio')}&nbsp;{(dataEntity?.total_acreditats || 0) + index + 1}
                </h5>
                {index > 0 && (
                  <Button
                    type="button"
                    variant="outlined"
                    icon={<CloseOutlined />}
                    onClick={() => {
                      const result = formik.values
                      accreditationTypeObj['requiredFields'].forEach((item) => {
                        delete result[`accreditation_${totalAccreditationsToAdd - 1}_${item}`]
                      })
                      formik.resetForm({
                        values: result,
                      })
                      setTotalAccreditationsToAdd(totalAccreditationsToAdd - 1)
                    }}
                  >
                    {t('delete')}
                  </Button>
                )}
              </div>

              {accreditationSchema &&
                accreditationSchema.data &&
                !accreditationSchema.loading &&
                accreditationTypeObj?.['orderFieldsForm']
                  .filter((item) => item.title !== 'declaracio_fieldset')
                  .map((item, indexItem) => {
                    return (
                      <div
                        className="fragment"
                        key={`accreditationFormSection_${item['title']}_${indexItem}`}
                      >
                        {item['fields'].map((key) => {
                          if (get(accreditationSchema, `data.properties[${key}]`, null)) {
                            const value = accreditationSchema.data.properties[key]
                            const keyForm = `accreditation_${index}_${key}`
                            const isFirstEmailField =
                              (dataEntity?.total_acreditats || 0) === 0 &&
                              key === 'correu' &&
                              index === 0

                            if (isFirstEmailField) {
                              formik.values[keyForm] = getEmailFromToken()
                            }
                            return (
                              <EditComponent
                                key={keyForm}
                                name={keyForm}
                                label={t(key)}
                                schema={value}
                                disabled={isFirstEmailField}
                                description={value.description ? t(`${key}_description`) : null}
                                value={formik.values[keyForm]}
                                required={
                                  isFirstEmailField
                                    ? false
                                    : (accreditationTypeObj['requiredFields'] ?? []).includes(key)
                                }
                                onBlur={() => {
                                  formik.setFieldTouched(keyForm)
                                }}
                                onChange={(ev) => {
                                  formik.setFieldValue(keyForm, ev)
                                  formik.validateForm()
                                }}
                                error={formik.touched[keyForm] && formik.errors[keyForm]}
                                dataTest={`inputFieldTest_${keyForm}`}
                              />
                            )
                          }
                        })}
                      </div>
                    )
                  })}
            </div>
          )
        })}
        <div>
          <Button
            type={'button'}
            dataTest={'add_more_accreditationsbtn'}
            onClick={() => {
              if (mustWarnBeforeAddingNextAccreditation) {
                setShowPaidAccreditationsModal(true)
                return
              }
              resetForm()
            }}
            icon={<AddProfessionalsIconOutline fill={'white'} />}
          >
            {t('add_more_accreditations')}
          </Button>
        </div>

        <Box mt={5} mb={3}>
          <h5>{t('declaracio_fieldset')}</h5>
          {renderInputAcceptRegulation('declaracio_acceptar')}
        </Box>
        <div>
          {formik.isSubmitting && <LoadingComponent />}
          {!readOnlyForm && !formik.isSubmitting && (
            <div className="footer-buttons">
              <Button type="submit" data-test="btnSubmitFormTest" disabled={formik.isSubmitting}>
                {t('save')}
              </Button>
            </div>
          )}
        </div>
      </form>
      <ModalInfo
        open={showPaidAccreditationsModal}
        setOpen={setShowPaidAccreditationsModal}
        text={
          <Trans
            i18nKey="alert_professional_acreditat"
            values={{ price: currentEdition?.getConfigData()?.preu_acreditacio }}
          />
        }
        icon={<AddProfessionalsIconOutline fill={'#273BF3'} />}
        handleOk={() => {
          onPaidAccreditationsAccepted?.()
          setShowPaidAccreditationsModal(false)
          resetForm()
        }}
        handleCancel={() => setShowPaidAccreditationsModal(false)}
        textOk={t('alert_professional_acreditat_ok')}
        textCancel={t('alert_professional_acreditat_cancel')}
      />
    </>
  )
}
